import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { usePrevious } from '@/utils/helpers/react.helper';
import { useCurrentMeetingDataContext } from '../meeting/context/CurrentMeetingDataContext';
import { MEETING_STATUS } from '../meeting/helpers/getMeetingStatus';
import { useCountdownTimer } from '../meeting/hooks/useCountdownTimer';
import useMeetingStatusSubscription from '../meeting/hooks/useMeetingStatusSubscription';
import { createOrUpdateHostTracking } from './createOorUpdateHostTracking';

const HOST_TRACKING_VERSION = '1.0.0';

export type HostTrackingObj = {
  version: string;
  events: HostTrackingEventLog[];
};

type HostTrackingEventLog<K extends HostTrackingEventName = HostTrackingEventName> = {
  eventName: K;
  extraData?: HostTrackingEventExtraDataMap[K];
  timestamp: number;
};

export enum HostTrackingEventName {
  SELECT_CASE = 'caseSelector.selectCase',

  SIDEBAR_OPEN_PATIENTCASE = 'sidebar.open.patientCase',
  SIDEBAR_OPEN_ASSET_VIEWER = 'sidebar.open.assetViewer',
  PATIENTCASE_MODAL_CLOSE = 'patientCaseModal.close',

  ANAMNESE_CLICK = 'anamnese.click',
  ANAMNESE_CLOSE = 'anamnese.close',

  OPEN_ITEM_ASSET_LIST = 'open.item.assetList',

  ASSET_VIEWER_CLOSE = 'assetViewer.close',
  ASSET_VIEWER_NEXTASSET = 'assetViewer.nextAsset',
  ASSET_VIEWER_PREVIOUSASSET = 'assetViewer.previousAsset',
  ASSET_VIEWER_PREVIEWCLICK = 'assetViewer.previewClick',
  ASSET_VIEWER_SWIPE = 'assetViewer.swipe',
  ASSET_VIEWER_VIDEO_PLAYPAUSE = 'assetViewer.video.playPause',
  ASSET_VIEWER_ZOOM = 'assetViewer.zoom',

  NAVIGATION_HOME = 'navigation.home',
  NAVIGATION_SWITCH_SLIDE = 'navigation.switchSlide',

  REFRESH_CLICK = 'refresh.click',
}

type HostTrackingEventExtraDataMap = {
  [HostTrackingEventName.SELECT_CASE]: { caseID: string };
  [HostTrackingEventName.SIDEBAR_OPEN_PATIENTCASE]: null;
  [HostTrackingEventName.SIDEBAR_OPEN_ASSET_VIEWER]: null;
  [HostTrackingEventName.PATIENTCASE_MODAL_CLOSE]: null;
  [HostTrackingEventName.ANAMNESE_CLICK]: null;
  [HostTrackingEventName.ANAMNESE_CLOSE]: null;
  [HostTrackingEventName.OPEN_ITEM_ASSET_LIST]: {
    // NOTE: I would like to add itemId here but the info item's do not have an id
    itemId?: string | null;
    assetIds: string[];
  };
  [HostTrackingEventName.ASSET_VIEWER_CLOSE]: null;
  [HostTrackingEventName.ASSET_VIEWER_NEXTASSET]: { fromAssetId: string; toAssetId: string };
  [HostTrackingEventName.ASSET_VIEWER_PREVIOUSASSET]: { fromAssetId: string; toAssetId: string };
  [HostTrackingEventName.ASSET_VIEWER_PREVIEWCLICK]: { fromAssetId: string; toAssetId: string };
  [HostTrackingEventName.ASSET_VIEWER_SWIPE]: {
    direction: 'left' | 'right';
    fromAssetId: string;
    toAssetId: string;
  };
  [HostTrackingEventName.ASSET_VIEWER_VIDEO_PLAYPAUSE]: { isPlaying: boolean };
  [HostTrackingEventName.ASSET_VIEWER_ZOOM]: { zoomLevel: number };
  [HostTrackingEventName.NAVIGATION_HOME]: null;
  [HostTrackingEventName.NAVIGATION_SWITCH_SLIDE]: { slideID: string };
  [HostTrackingEventName.REFRESH_CLICK]: null;
};

type HostTrackingContextType = {
  logHostAction: <K extends HostTrackingEventName>(
    eventName: K,
    extraData: HostTrackingEventExtraDataMap[K]
  ) => void;
};

const LOCALSTORAGE_HOST_TRACKING_KEY = 'hostTrackingEvents:meetingId';

export const HostTrackingContext = createContext<HostTrackingContextType | undefined>(undefined);

type Props = PropsWithChildren;

// NOTE: If multiple hosts are present this will not behave correctly, both hosts will override each other's events.
export default function HostTrackingContextProvider({ children }: Props) {
  const { meeting, isUserHost } = useCurrentMeetingDataContext();
  const [events, setEvents] = useState<HostTrackingEventLog[]>([]);
  const meetingStatus = useMeetingStatusSubscription(meeting?.id);
  const previousMeetingStatus = usePrevious(meetingStatus);
  const previousMeetingStartDate = usePrevious(meeting?.startDate);

  const [saveTrigger, triggerSave] = useState<Date>(new Date());

  const { resetTimer } = useCountdownTimer({
    autoStart: true,
    duration: 30000,
    onEnd: () => {
      if (!isUserHost) return;
      console.log('DEBUG >> Saving host tracking events due to timer end');
      triggerSave(new Date());
    },
  });

  const saveToBackend = useCallback(
    async (eventsToSave: HostTrackingEventLog[]) => {
      if (!meeting?.id || !isUserHost) return;
      if (eventsToSave.length === 0 && meeting.endDate) {
        console.log('DEBUG >> Preventing save to backend, meeting has ended');
        return;
      }

      console.log('DEBUG >> Saving host tracking events to backend', eventsToSave);
      await createOrUpdateHostTracking(meeting.id, {
        version: HOST_TRACKING_VERSION,
        events: eventsToSave,
      });
    },
    [meeting?.id, meeting?.endDate, isUserHost]
  );

  const logHostAction = useCallback(
    <K extends HostTrackingEventName>(
      eventName: K,
      extraData: HostTrackingEventExtraDataMap[K]
    ) => {
      if (!isUserHost) return;
      const newEvent: HostTrackingEventLog = { eventName, timestamp: Date.now() };
      console.log('DEBUG >> Adding host action');
      if (extraData) {
        newEvent.extraData = extraData;
      }
      setEvents((prevEvents) => [...prevEvents, newEvent]);
    },
    [isUserHost]
  );

  // NOTE: we have to pass through a useEffect to ensure the events variables is up to date
  // the other option would be to do this directly inside the onEnd of the timer but the events
  // variable would not be up to date at that point
  useEffect(() => {
    saveToBackend(events);
    resetTimer();
  }, [saveTrigger]);

  // NOTE: Responsible for saving events at the end of the meeting
  useEffect(() => {
    if (!meeting?.id || !isUserHost) return;
    if (
      meetingStatus === MEETING_STATUS.USER_RATING &&
      previousMeetingStatus !== MEETING_STATUS.USER_RATING
    ) {
      triggerSave(new Date());
    }
  }, [meetingStatus]);

  // NOTE: Responsible for resetting events when the meeting restarts
  useEffect(() => {
    if (meeting?.startDate !== previousMeetingStartDate && previousMeetingStartDate !== undefined) {
      console.log(
        'DEBUG >> Meeting start date changed, resetting host tracking events',
        meeting.startDate,
        previousMeetingStartDate
      );

      const key = LOCALSTORAGE_HOST_TRACKING_KEY.replace('meetingId', meeting.id);
      localStorage.removeItem(key);
      saveToBackend([]);
      setEvents([]);
      resetTimer();
    }
  }, [meeting?.startDate]);

  // NOTE: when we join a new meeting we reset the events (reload for example)
  useEffect(() => {
    if (!meeting?.id || !isUserHost) return;
    const key = LOCALSTORAGE_HOST_TRACKING_KEY.replace('meetingId', meeting.id);
    const localStorageItem = localStorage.getItem(key);
    if (!localStorageItem) {
      console.log('DEBUG >> No local storage item found, initializing empty events');
      setEvents([]);
      return;
    }
    const obj = JSON.parse(localStorageItem) as HostTrackingObj;
    setEvents(obj.events);
  }, [meeting?.id]);

  // NOTE: save to localstorage just in case
  useEffect(() => {
    if (!isUserHost) return;
    if (events.length > 0 && meeting?.id) {
      const key = LOCALSTORAGE_HOST_TRACKING_KEY.replace('meetingId', meeting.id);
      localStorage.setItem(
        key,
        JSON.stringify({
          version: HOST_TRACKING_VERSION,
          events,
        } satisfies HostTrackingObj)
      );
      console.log('DEBUG >> all events', events);
    }
  }, [events, isUserHost]);

  return (
    <HostTrackingContext.Provider value={{ logHostAction }}>
      {children}
    </HostTrackingContext.Provider>
  );
}

export const useHostTrackingContext = (): HostTrackingContextType => {
  const context = useContext(HostTrackingContext);
  if (context === undefined) {
    return {
      logHostAction: () => {},
    };
  }
  return context;
};
